import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useCreateSeatingPlan } from 'queries/seatingPlan.query'
import { useGetVenues } from 'queries/venue.query'

export function CreatePlanModal({ isOpen, onClose }) {
  const inputColor = useColorModeValue('gray.800', 'white')
  const toast = useToast()
  const navigate = useNavigate()
  const createPlanMutation = useCreateSeatingPlan()
  const { data: venuesData } = useGetVenues(1, 100)

  const venues = React.useMemo(() => {
    if (venuesData?.data) return venuesData.data
    if (Array.isArray(venuesData)) return venuesData
    return []
  }, [venuesData])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      venue_id: '',
      width: 900,
      height: 900,
    },
  })

  const onSubmit = async (formData) => {
    try {
      const defaultPlan = {
        name: formData.name,
        venue_id: formData.venue_id ? Number(formData.venue_id) : null,
        plan_json: {
          name: formData.name,
          categories: [
            { name: 'Category I', color: '#E03400' },
            { name: 'Category II', color: '#BB36D3' },
            { name: 'Category III', color: '#3A863D' },
          ],
          zones: [
            {
              name: 'Main Floor',
              position: { x: 0, y: 0 },
              rows: [],
              areas: [
                {
                  shape: 'rectangle',
                  color: '#4A5568',
                  border_color: '#CBD5E0',
                  rotation: 0,
                  position: { x: 300, y: 50 },
                  text: { text: 'STAGE', color: '#FFFFFF', position: { x: 150, y: 40 } },
                  rectangle: { width: 300, height: 80 },
                },
              ],
            },
          ],
          size: {
            width: Number(formData.width) || 900,
            height: Number(formData.height) || 900,
          },
        },
        background_settings: {
          opacity: 0.4,
          x: 0,
          y: 0,
          width: Number(formData.width) || 900,
          height: Number(formData.height) || 900,
        },
      }

      const res = await createPlanMutation.mutateAsync(defaultPlan)
      const newPlanId = res?.data?.id || res?.id
      toast({
        title: 'Plan initialized',
        description: 'Opening Visual Seating Plan Editor...',
        status: 'success',
        duration: 2000,
      })
      reset()
      onClose()
      if (newPlanId) {
        navigate(`/admin/seating-plans/${newPlanId}/edit`)
      }
    } catch (err) {
      toast({
        title: 'Failed to create plan',
        description: err?.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>Create Seating Plan</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={errors.name}>
              <FormLabel>Plan Title</FormLabel>
              <Input
                color={inputColor}
                placeholder="e.g. Dietzenbach Hauptsaal - Concert Setup"
                {...register('name', { required: 'Plan title is required' })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Associated Venue</FormLabel>
              <Select placeholder="Select a venue..." color={inputColor} {...register('venue_id')}>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.city})
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Canvas Dimensions (Width x Height)</FormLabel>
              <VStack spacing={2}>
                <Input
                  type="number"
                  placeholder="Width (default 900)"
                  color={inputColor}
                  {...register('width', { valueAsNumber: true })}
                />
                <Input
                  type="number"
                  placeholder="Height (default 900)"
                  color={inputColor}
                  {...register('height', { valueAsNumber: true })}
                />
              </VStack>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="brand" type="submit" isLoading={isSubmitting || createPlanMutation.isPending}>
            Launch Visual Editor
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
